/*
  ListContact.js - List Component for listing Users in a Cost Centre
*/

import {
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';

// COMPONENTS
import Loader from '../../common/Loader';
import Paginator from '../../common/Paginator';

// I18N
import formattedMessages from '../../../messages/account/CostCenterContactsFormattedMessages';

// CONFIG
import Config from '../../../Config';

// NETWORK
import useCostCentreRequest from '../../../requests/CostCentreRequest';

const CostCenterContacts = ({ centre, errorHandler, callBack }) => {
  const { getUsersForCostCentre } = useCostCentreRequest(errorHandler);
  const getUsersRef = useRef(getUsersForCostCentre);

  const [state, setState] = useState({
    pageNumber: 1,
    error: null,
    displayLetters: [],
    filterLetter: '',
    loaded: false,
    users: [],
    costCenterId: null,
  });

  const pageLength = Config.defaultPL;

  const sortList = useCallback((data) => {
    if (!Array.isArray(data)) return [];

    return [...data].sort((a, b) => {
      const nameA = (a?.user_name || '').toLowerCase();
      const nameB = (b?.user_name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, []);

  // Update ref when the function changes
  useEffect(() => {
    getUsersRef.current = getUsersForCostCentre;
  }, [getUsersForCostCentre]);

  // Load users only when centre ID changes
  useEffect(() => {
    let isMounted = true;

    const loadUsers = async () => {
      if (!centre?.cost_centre_id) return;

      try {
        const users = await getUsersRef.current(centre.cost_centre_id);

        if (isMounted) {
          const sortedList = sortList(users || []);
          setState((prev) => ({
            ...prev,
            users: sortedList,
            loaded: true,
            costCenterId: centre.cost_centre_id,
          }));
        }
      } catch (error) {
        console.error('Error loading users:', error);
        if (isMounted) {
          setState((prev) => ({
            ...prev,
            error: 'Error loading users',
            loaded: true,
            users: [],
          }));
        }
      }
    };

    loadUsers();

    return () => {
      isMounted = false;
    };
  }, [centre?.cost_centre_id, sortList]); // Remove costCentreRequest from dependencies

  const handleLetterFilter = useCallback((letter) => {
    setState((prev) => ({
      ...prev,
      filterLetter: prev.filterLetter !== letter ? letter : '',
      pageNumber: 1,
    }));
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setState((prev) => ({
      ...prev,
      pageNumber: newPage,
    }));
  }, []);

  const processUserList = useCallback(() => {
    const { users, pageNumber, filterLetter, costCenterId } = state;

    if (!Array.isArray(users))
      return { displayList: [], fullList: [], letters: [] };

    const letterMap = new Map();
    const letters = [];
    const fullList = [];

    // Filter and collect letters
    users.forEach((user) => {
      const firstLetter = user.user_name?.charAt(0)?.toUpperCase();
      if (firstLetter) {
        if (
          !letterMap.has(firstLetter) &&
          costCenterId === user.cost_centre_id
        ) {
          letterMap.set(firstLetter, true);
          letters.push(firstLetter);
        }

        if (
          !state.filterLetter ||
          state.filterLetter.toLowerCase() ===
            user.user_name.charAt(0).toLowerCase()
        ) {
          fullList.push(user);
        }
      }
    });

    // Paginate
    const displayList = fullList.slice(
      (pageNumber - 1) * pageLength,
      (pageNumber - 1) * pageLength + pageLength
    );

    return {
      displayList,
      fullList,
      letters: letters.sort(),
    };
  }, [state, pageLength]);

  const { displayList, fullList, letters } = processUserList();

  if (!state.loaded) {
    return <Loader />;
  }

  return (
    <>
      {/* Letter Filter */}
      <Stack direction="row" sx={{ marginTop: 2, marginBottom: 0.25 }}>
        {(letters || []).map((letter) => (
          <Button
            key={letter}
            variant={'text'}
            onClick={() => handleLetterFilter(letter)}
            size="small"
            sx={{
              minWidth: 'auto',
              padding: 0.25,
              color:
                state.filterLetter === letter
                  ? 'primary.main'
                  : 'secondary.light',
            }}
          >
            {letter}
          </Button>
        ))}
      </Stack>

      {/* Users Table */}
      <TableContainer
        sx={{ marginBottom: 4, overflowX: 'auto', whiteSpace: 'nowrap' }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{formattedMessages.name}</TableCell>
              <TableCell>{formattedMessages.email}</TableCell>
              <TableCell>{formattedMessages.cell}</TableCell>
              <TableCell>{formattedMessages.status}</TableCell>
              <TableCell>{formattedMessages.maxDailyMessage}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayList.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>
                  <Typography>No users found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayList.map((user) => (
                <TableRow
                  key={user.customer_user_id}
                  onClick={() => callBack?.(user)}
                >
                  <TableCell>{user.user_name}</TableCell>
                  <TableCell>{user.user_email}</TableCell>
                  <TableCell>{user.user_tn}</TableCell>
                  <TableCell>
                    {user.user_status === 'A' ? 'Active' : 'Inactive'}
                  </TableCell>
                  <TableCell>{user.max_sms_per_day || '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Paginator
        pageNumber={state.pageNumber}
        itemCount={fullList.length}
        pageLength={pageLength}
        changeEvent={handlePageChange}
        disabled={!state.loaded}
        showTotal="Users Found"
      />
    </>
  );
};

export default CostCenterContacts;
